import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigationType } from "react-router-dom";
import { useRouteMotion } from "@/lib/useRouteMotion";
import { useRouteScroll } from "@/lib/useRouteScroll";

const navItems = [["Research", "/research"], ["Strategy", "/strategy"], ["Saved", "/saved"], ["About", "/about"]];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const menuButton = useRef(null);
  const header = useRef(null);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => { setMobile(query.matches); if (!query.matches) setOpen(false); };
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => { if (event.key === "Escape") { setOpen(false); menuButton.current?.focus(); } };
    const closeOutside = (event) => { if (!header.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOutside);
    return () => { document.removeEventListener("keydown", closeOnEscape); document.removeEventListener("pointerdown", closeOutside); };
  }, [open]);
  return (
    <header ref={header} className="site-header" data-testid="site-header">
      <div className="shell nav-shell">
        <NavLink className="wordmark" to="/" data-testid="home-wordmark-link">FU Research</NavLink>
        <button ref={menuButton} className="menu-toggle" type="button" aria-expanded={open} aria-controls="primary-navigation" aria-label={open ? "Close navigation" : "Toggle navigation"} onClick={() => setOpen((value) => !value)} data-testid="mobile-navigation-toggle"><span /><span /></button>
        <nav id="primary-navigation" className={`main-nav ${open ? "is-open" : ""}`} aria-label="Primary navigation" aria-hidden={mobile ? !open : undefined} inert={mobile && !open ? true : undefined} data-testid="primary-navigation">
          {navItems.map(([label, path]) => <NavLink key={path} to={path} className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`} data-testid={`navigation-${label.toLowerCase()}-link`}>{label}</NavLink>)}
        </nav>
      </div>
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="site-footer" data-testid="site-footer">
    <div className="shell footer-grid">
      <div><NavLink className="footer-wordmark" to="/" data-testid="footer-home-link">FU Research</NavLink><p data-testid="footer-location">Sydney, Australia</p></div>
      <p data-testid="footer-description">Independent long-term research</p>
      <nav className="footer-legal" aria-label="Legal" data-testid="footer-legal-section">
        <p className="footer-label" data-testid="footer-legal-label">Legal</p>
        <NavLink to="/legal/disclaimer" data-testid="footer-disclaimer-link">Disclaimer</NavLink>
        <NavLink to="/legal/privacy-policy" data-testid="footer-privacy-policy-link">Privacy Policy</NavLink>
        <NavLink to="/legal/terms-of-use" data-testid="footer-terms-of-use-link">Terms of Use</NavLink>
      </nav>
    </div>
  </footer>
);

export const SiteLayout = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const mainRef = useRef(null);
  useRouteScroll(mainRef, location, navigationType);
  useRouteMotion(mainRef, location, navigationType);
  return <div className="site-frame">
    <a className="skip-link" href="#main-content" data-testid="skip-to-content-link">Skip to content</a>
    <SiteHeader />
    <main ref={mainRef} key={location.pathname} id="main-content" tabIndex={-1} data-testid="page-main-content"><Outlet /></main>
    <SiteFooter />
  </div>;
};